import {
  FINANCING_YEAR_65,
  FINANCING_YEAR_75,
  FinancingLoanType,
  INCOME_HAIRCUT_PERCENT,
  MAX_DEPOSIT,
  MIN_DEPOSIT,
} from '@/constants/common';
import { getNumberWithoutPercent } from '.';
import config from '@/config/config.json';
import {
  DEFAULT_EC_LOAN_DETAILS_AGE_65,
  DEFAULT_EC_LOAN_DETAILS_AGE_75,
  DEFAULT_HDB_LOAN_DETAILS_AGE_65,
  DEFAULT_HDB_LOAN_DETAILS_AGE_75,
  PropertyType,
} from '@/constants/hdb-resale/msr';
import { DependenceFieldName } from '@/features/private-resale/purchase-resale/components/PotentialExpenses';

// Excel PMT function
export const pmt = (rate: number, nper: number, pv: number, fv: number = 0, type: number = 0) => {
  if (rate === 0) {
    return -(pv + fv) / nper;
  }

  const pvif = Math.pow(1 + rate, nper);
  let pmt = (rate / (pvif - 1)) * -(pv * pvif + fv);

  if (type === 1) {
    pmt /= 1 + rate;
  }

  return pmt;
};

// Excel PV function
export const pv = (r: number, n: number, y: number, f: number, t: number) => {
  let retval = 0;

  if (r === 0) {
    retval = -1 * (n * y + f);
  } else {
    const r1 = r + 1;

    retval = (((1 - Math.pow(r1, n)) / r) * (t ? r1 : 1) * y - f) / Math.pow(r1, n);
  }

  return retval;
};

//https://www.experts-exchange.com/articles/1948/A-Guide-to-the-PMT-FV-IPMT-and-PPMT-Functions.html
// Excel IPMT function
export const ipmt = (pv: number, pmt: number, rate: number, per: number) => {
  const tmp = Math.pow(1 + rate, per);

  return 0 - (pv * tmp * rate + pmt * (tmp - 1));
};

// Excel PPMT function
export const ppmt = (
  rate: number,
  per: number,
  nper: number,
  pv: number,
  fv: number = 0,
  type: number = 0,
  // eslint-disable-next-line max-params
) => {
  if (per < 1 || per >= nper + 1) {
    return 0;
  }

  const pmt1 = pmt(rate, nper, pv, fv, type);
  const ipmt1 = ipmt(pv, pmt1, rate, per - 1);

  return pmt1 - ipmt1;
};

// BSD for Residential Property
export const getBsdPrice = (purchasePrice: number): number => {
  return getBSD(purchasePrice, 'residential_property');
};

// BSD for Non-Residential Property
export const getCIBSD = (purchasePrice: number): number => {
  return getBSD(purchasePrice, 'non_residential_property');
};

export const getBSD = (
  purchasePrice: number,
  propertyType: 'residential_property' | 'non_residential_property',
): number => {
  if (purchasePrice <= 0) {
    return 0;
  }

  const tiers = config.bsd_in_percent[propertyType];
  let remaining = purchasePrice;
  let previousCap = 0;
  let totalTax = 0;

  for (const tier of tiers) {
    const { cap, rate } = tier;

    if (cap === null || purchasePrice <= cap) {
      const taxableAmount = remaining;

      totalTax += taxableAmount * (rate / 100);
      break;
    }

    const tierAmount = cap - previousCap;

    totalTax += tierAmount * (rate / 100);
    remaining -= tierAmount;
    previousCap = cap;
  }

  return Math.round(totalTax);
};

export const getCPF = (loanToValue: number, cash: number) => {
  const value = 100 - loanToValue - cash;

  return value >= 0 ? value : 0;
};

export const getPriceByRate = (price: number, rate: number) => {
  return price * (rate / 100);
};

export const updateDependenceValueFee = <TField extends DependenceFieldName>({
  currentValue,
  currentField,
  dependenceField,
  min,
  updateField,
  minDeposit = MIN_DEPOSIT,
  maxDeposit = MAX_DEPOSIT,
}: {
  currentValue: number;
  currentField: TField;
  dependenceField: {
    nameField: TField;
    value: number;
  };
  min: number;
  updateField: (fieldName: TField, value: number) => void;
  minDeposit?: number;
  maxDeposit?: number;
}) => {
  const deposit = currentValue + dependenceField.value;

  if (currentValue < min) {
    const newValue = dependenceField.value >= minDeposit ? min : minDeposit - dependenceField.value;

    updateField(currentField, newValue);

    return;
  }

  if (deposit < minDeposit) {
    updateField(dependenceField.nameField, minDeposit - currentValue);

    return;
  }

  if (deposit > maxDeposit) {
    updateField(dependenceField.nameField, maxDeposit - currentValue);

    return;
  }
};

export const getTotalMonthlyIncomeForEach = (income: {
  monthlyFixedIncome: number;
  monthlyVariableIncome: number;
  monthlyOtherIncome?: number;
}) => {
  const totalVariableIncome =
    income.monthlyVariableIncome * getNumberWithoutPercent(INCOME_HAIRCUT_PERCENT);

  const totalOtherIncome =
    (income?.monthlyOtherIncome ?? 0) * getNumberWithoutPercent(INCOME_HAIRCUT_PERCENT);

  const result = income.monthlyFixedIncome + totalVariableIncome + totalOtherIncome;

  return parseFloat(result.toFixed(2));
};

//Income Weighted Average Age
export const getIWAA = (
  iwaaProfiles: {
    age: number;
    monthlyFixedIncome: number;
    monthlyVariableIncome: number;
    monthlyOtherIncome?: number;
  }[],
) => {
  if (iwaaProfiles.length === 0) {
    return 0;
  }

  const { totalIncomeAge, totalIncome } = iwaaProfiles.reduce(
    (acc, profile) => {
      const income = getTotalMonthlyIncomeForEach({
        monthlyFixedIncome: +profile.monthlyFixedIncome,
        monthlyVariableIncome: +profile.monthlyVariableIncome,
        monthlyOtherIncome: profile?.monthlyOtherIncome,
      });
      const age = +profile.age;

      if (age > 0) {
        acc.totalIncomeAge += age * income;
        acc.totalIncome += income;
      }

      return acc;
    },
    { totalIncomeAge: 0, totalIncome: 0 },
  );

  return totalIncome === 0 ? 0 : Math.ceil(totalIncomeAge / totalIncome);
};

export const getMaxLoanYears = (
  financingLoanType: number,
  iwaa: number,
  propertyType?: PropertyType, // Optional
): number => {
  if (financingLoanType === 0) {
    return iwaa;
  }

  // Property-specific config
  const propertyConfig: Record<PropertyType, Record<number, number>> = {
    [PropertyType.HDB]: {
      [FinancingLoanType.sixtyFiveYearLoan]: DEFAULT_HDB_LOAN_DETAILS_AGE_65.LOAN_TENURE_MAX,
      [FinancingLoanType.seventyFiveYearLoan]: DEFAULT_HDB_LOAN_DETAILS_AGE_75.LOAN_TENURE_MAX,
    },
    [PropertyType.EC]: {
      [FinancingLoanType.sixtyFiveYearLoan]: DEFAULT_EC_LOAN_DETAILS_AGE_65.LOAN_TENURE_MAX,
      [FinancingLoanType.seventyFiveYearLoan]: DEFAULT_EC_LOAN_DETAILS_AGE_75.LOAN_TENURE_MAX,
    },
  };

  // Fallback default config (when propertyType is undefined)
  const defaultConfig: Record<number, number> = {
    [FinancingLoanType.sixtyFiveYearLoan]: FINANCING_YEAR_65.LOAN_TENURE.MAX,
    [FinancingLoanType.seventyFiveYearLoan]: FINANCING_YEAR_75.LOAN_TENURE.MAX,
  };

  const maxTenure = propertyType
    ? (propertyConfig[propertyType]?.[financingLoanType] ?? 0)
    : (defaultConfig[financingLoanType] ?? 0);

  return Math.min(Math.abs(financingLoanType - iwaa), maxTenure);
};

export const getMinCpfOaOrCashPercent = (ltv: number, minCash: number) => {
  return 100 - (ltv + minCash);
};

export const getCOV = ({
  purchasePrice,
  valuationPrice,
}: {
  purchasePrice: number;
  valuationPrice: number;
}) => {
  const cov = purchasePrice - valuationPrice;

  return cov;
};

export const getDepositBreakdown = ({
  purchasePrice,
  optionFee,
  optionExerciseFee,
}: {
  purchasePrice: number;
  optionFee: number;
  optionExerciseFee: number;
}) => {
  const optionFeeAmount = (optionFee * purchasePrice) / 100;
  const optionExerciseFeeAmount = (optionExerciseFee * purchasePrice) / 100;

  return { optionFeeAmount, optionExerciseFeeAmount };
};

export const getDepositBreakdownPercentage = ({
  purchasePrice,
  optionFeeAmount,
  optionExerciseFeeAmount,
}: {
  purchasePrice: number;
  optionFeeAmount: number;
  optionExerciseFeeAmount: number;
}) => {
  if (purchasePrice === 0) {
    return { optionFee: 0, optionExerciseFee: 0 };
  }

  const optionFee = (100 * optionFeeAmount) / purchasePrice;
  const optionExerciseFee = (100 * optionExerciseFeeAmount) / purchasePrice;

  return { optionFee, optionExerciseFee };
};

export const getABSDAmount = ({ purchasePrice, absd }: { purchasePrice: number; absd: number }) => {
  const amount = (absd * purchasePrice) / 100;

  return amount;
};

export const getLoanAmount = ({ valuationPrice, ltv }: { valuationPrice: number; ltv: number }) => {
  const amount = (ltv * valuationPrice) / 100;

  return amount;
};

export const getMonthlyInstalment = ({
  interestRate,
  totalMonth,
  loanAmount,
}: {
  interestRate: number;
  totalMonth: number;
  loanAmount: number;
}) => {
  const amount = -pmt(interestRate / 100 / 12, totalMonth, loanAmount);

  return amount;
};

export const getServiceCommissionAmount = (
  sellingPrice: number,
  serviceCommissionPercent: number,
  gstPercent: number,
) => {
  const serviceCommissionAmount = (serviceCommissionPercent * sellingPrice) / 100;
  const gstAmount = (gstPercent * serviceCommissionAmount) / 100;
  const value = serviceCommissionAmount + gstAmount;

  return value;
};

export const getInterestAmountMonthly = ({
  loanAmount,
  interestRate,
}: {
  loanAmount: number;
  interestRate: number;
}) => {
  const amount = (loanAmount * interestRate) / 100 / 12;

  return amount;
};
